import { DomainConfigInputSchema } from './domain-config.schema';
import { hasAllowlistPathRules } from './domain-config-sync.util';
import {
  DomainSourceSchema,
  type DomainConfig,
  type DomainPathRule,
  type DomainSourceType,
} from '@terra/shared-schemas-orchestrator';
import type { z } from 'zod';

type DomainConfigInput = z.infer<typeof DomainConfigInputSchema>;

export type DomainConfigPathRuleInput = DomainPathRule;

export type DomainConfigInputSource = Omit<
  DomainConfig,
  'hasWaf' | 'createdAt' | 'enabledAt' | 'pathRules'
> & {
  pathRules?: DomainPathRule[];
  hasWaf?: boolean | null;
  createdAt?: Date;
  enabledAt?: Date | null;
};

export type DomainConfigPersistedFields = {
  hasWaf?: boolean | null;
  createdAt?: Date;
  enabledAt?: Date | null;
};

export type DomainConfigParsedInput = Omit<
  DomainConfigInput,
  'hasWaf' | 'createdAt' | 'enabledAt'
> & {
  hasWaf: boolean | null;
  createdAt: Date;
  enabledAt: Date | null;
};

export const resolveDomainSource = (
  source: DomainSourceType | null | undefined,
): DomainSourceType => source ?? DomainSourceSchema.enum.USER_PROVIDED;

export const isUserProvidedDomainSource = (
  source: DomainSourceType | null | undefined,
): boolean =>
  resolveDomainSource(source) === DomainSourceSchema.enum.USER_PROVIDED;

export const resolveDomainConfigPersistedFields = ({
  incoming,
  existing,
}: {
  incoming: DomainConfigPersistedFields;
  existing?: DomainConfigPersistedFields;
}): {
  hasWaf: boolean | null;
  createdAt: Date;
  enabledAt: Date | null;
} => ({
  hasWaf:
    incoming.hasWaf !== undefined
      ? incoming.hasWaf
      : (existing?.hasWaf ?? null),
  createdAt: incoming.createdAt ?? existing?.createdAt ?? new Date(),
  enabledAt:
    incoming.enabledAt !== undefined
      ? incoming.enabledAt
      : (existing?.enabledAt ?? null),
});

export const parseDomainConfigInput = (
  entry: z.input<typeof DomainConfigInputSchema>,
): DomainConfigInput => DomainConfigInputSchema.parse(entry);

export const toDomainConfigInputList = (
  domains: DomainConfigInputSource[],
): DomainConfigInput[] =>
  domains.flatMap(
    ({
      domain,
      source,
      isInFunctionalityScope,
      isInTestingScope,
      isThirdParty,
      pathRules,
      hasWaf,
      createdAt,
      enabledAt,
    }) => {
      const normalizedPathRules = pathRules ?? [];
      const parsed = DomainConfigInputSchema.safeParse({
        domain,
        source: resolveDomainSource(source),
        ...(hasAllowlistPathRules(normalizedPathRules)
          ? {
              isInFunctionalityScope,
              isInTestingScope: null,
              isThirdParty,
            }
          : { isInFunctionalityScope, isInTestingScope, isThirdParty }),
        pathRules: normalizedPathRules,
        hasWaf,
        createdAt,
        enabledAt,
      });

      if (parsed.success) {
        return [parsed.data];
      }

      console.error('Dropping invalid application domain', {
        domain,
        error: parsed.error,
      });

      return [];
    },
  );

export const toPersistedDomainConfigList = ({
  domains,
  previousDomains = [],
}: {
  domains: DomainConfigInputSource[];
  previousDomains?: DomainConfigInputSource[];
}): DomainConfigParsedInput[] => {
  const previousByDomain = new Map(
    previousDomains.map((entry) => [entry.domain.toLowerCase(), entry]),
  );

  return toDomainConfigInputList(domains).map((entry) => ({
    ...entry,
    ...resolveDomainConfigPersistedFields({
      incoming: entry,
      existing: previousByDomain.get(entry.domain.toLowerCase()),
    }),
  }));
};
